import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useReactTable,
  getCoreRowModel,
  createColumnHelper,
} from '@tanstack/react-table';
import {
  Activity,
  Server,
  Cable,
  Shield,
  Play,
  Square,
  Settings,
  Eye,
  Trash2,
  Users,
  Plus,
} from 'lucide-react';
import { message } from 'antd';
import { messageI18n, tToast } from '../../../helpers/i18n/toastI18n';

import { Badge } from '../../ComponnentsUI/common/Badge';
import { Table } from '../../ComponnentsUI/common/Table';
import { Button } from '../../ComponnentsUI/common/Button';
import {
  ActionMenu,
  ActionMenuItem,
} from '../../ComponnentsUI/common/ActionMenu';

import {
  useStartApplicationEntity,
  useStopApplicationEntity,
  useDeleteApplicationEntity,
  useUpdateComprehensiveApplicationEntity,
} from '../../../helpers/api/pacs/pacs';
import AECreationOptimalForm from './ae-creation-optimal';
import {
  ConfigureAEModal,
  AEViewModal,
  AcceptedCallingAETitlesModal,
  DeleteConfirmationModal,
} from './modals';
import { getAEStatusBadge } from './utils/status-badges';
import { defaultSyntaxConfig } from './resources/dicom-config';
import type { AEStatus } from './types';
import type { DicomSyntaxConfig, AbstractSyntaxConfig } from './types/dicom';

interface DeviceInfo {
  id: number;
  deviceName: string;
  description: string | null;
  manufacturer: string | null;
  manufacturerModelName: string | null;
  status: string;
}

interface ConnectionInfo {
  id: number;
  connectionName: string;
  hostname: string;
  port: number;
  tlsEnabled: boolean;
  status: string;
}

interface TransferCapability {
  id: number;
  sopClass: string;
  role: string;
  transferSyntaxes: string;
  commonName: string;
  enabled: boolean;
  priority: number;
  transferSyntaxArray: string[];
}

interface ApplicationEntity {
  id: number;
  aeTitle: string;
  name: string | null;
  description: string;
  manufacturer: string | null;
  model: string | null;
  aeRole: 'SCP' | 'SCU' | 'BOTH';
  aeType: string | null;
  status: 'ACTIVE' | 'INACTIVE';
  installed: boolean;
  associator: boolean;
  associateAcceptor: boolean;
  deviceId: number;
  connectionId: number;
  createdAt: string;
  updatedAt: string;
  device: DeviceInfo;
  connection: ConnectionInfo;
  acceptedCallingAeTitles: any[];
  supportedServices: unknown[];
  transferCapabilities: TransferCapability[];
}

const mapAEStatus = (backendStatus: string): AEStatus => {
  switch (backendStatus.toUpperCase()) {
    case 'ACTIVE':
      return 'running';
    case 'INACTIVE':
      return 'stopped';
    default:
      return 'stopped';
  }
};

const transformAETransferCapabilities = (
  transferCapabilities: TransferCapability[]
): DicomSyntaxConfig => {
  const sopClassUidToConfigKey: Record<string, keyof DicomSyntaxConfig> = {
    '1.2.840.10008.5.1.4.1.1.7': 'secondaryCaptureImageStorage',
    '1.2.840.10008.5.1.4.1.1.6.1': 'ultrasoundImageStorage',
    '1.2.840.10008.5.1.4.1.1.3.1': 'ultrasoundMultiFrameImageStorage',
    '1.2.840.10008.1.1': 'verificationSopClass',
  };

  const transferSyntaxUidToConfigKey: Record<
    string,
    keyof AbstractSyntaxConfig
  > = {
    '1.2.840.10008.1.2.2': 'explicitVrBigEndian',
    '1.2.840.10008.1.2.1': 'explicitVrLittleEndian',
    '1.2.840.10008.1.2': 'implicitVrLittleEndian',
    '1.2.840.10008.1.2.4.91': 'jpeg2000ImageCompression',
    '1.2.840.10008.1.2.4.90': 'jpeg2000ImageCompressionLosslessOnly',
    '1.2.840.10008.1.2.4.50': 'jpegBaselineProcess1',
    '1.2.840.10008.1.2.4.70':
      'jpegLosslessNonHierarchicalFirstOrderPredictionProcess14SelectionValue1',
    '1.2.840.10008.1.2.4.80': 'jpegLsLosslessImageCompression',
    '1.2.840.10008.1.2.4.81': 'jpegLsLossyNearLosslessImageCompression',
  };

  const initializeAbstractSyntax = (): AbstractSyntaxConfig => ({
    explicitVrBigEndian: 'rejected',
    explicitVrLittleEndian: 'rejected',
    implicitVrLittleEndian: 'rejected',
    jpeg2000ImageCompression: 'rejected',
    jpeg2000ImageCompressionLosslessOnly: 'rejected',
    jpegBaselineProcess1: 'rejected',
    jpegLosslessNonHierarchicalFirstOrderPredictionProcess14SelectionValue1:
      'rejected',
    jpegLsLosslessImageCompression: 'rejected',
    jpegLsLossyNearLosslessImageCompression: 'rejected',
  });

  const syntaxConfig: DicomSyntaxConfig = {
    secondaryCaptureImageStorage: initializeAbstractSyntax(),
    ultrasoundImageStorage: initializeAbstractSyntax(),
    ultrasoundMultiFrameImageStorage: initializeAbstractSyntax(),
    verificationSopClass: {
      explicitVrBigEndian: 'rejected',
      explicitVrLittleEndian: 'rejected',
      implicitVrLittleEndian: 'rejected',
    },
  };

  transferCapabilities.forEach((capability) => {
    const configKey = sopClassUidToConfigKey[capability.sopClass];

    if (configKey && capability.enabled) {
      const transferSyntaxes =
        capability.transferSyntaxArray ||
        (capability.transferSyntaxes
          ? capability.transferSyntaxes.split(',')
          : []);

      transferSyntaxes.forEach((transferSyntaxUid) => {
        const transferSyntaxKey =
          transferSyntaxUidToConfigKey[transferSyntaxUid.trim()];

        if (transferSyntaxKey) {
          if (configKey === 'verificationSopClass') {
            const verificationConfig =
              syntaxConfig.verificationSopClass as Partial<AbstractSyntaxConfig>;
            if (transferSyntaxKey in verificationConfig) {
              (verificationConfig as Record<string, string>)[
                transferSyntaxKey
              ] = 'accepted';
            }
          } else {
            (syntaxConfig[configKey] as AbstractSyntaxConfig)[
              transferSyntaxKey
            ] = 'accepted';
          }
        }
      });
    }
  });

  return syntaxConfig;
};

const columnHelper = createColumnHelper<ApplicationEntity>();

export default function ApplicationEntitiesTable({
  devicesDataWithConnections,
  isLoadingDevicesWithConnections,
  address,
  applicationEntitiesData,
  isLoadingApplicationEntities,
  refetchApplicationEntities,
  pagination,
  setPagination,
  sortConfig,
  setSortConfig,
  selectedDeviceId,
  refetchDevicesWithConnections,
}: {
  devicesDataWithConnections: any;
  isLoadingDevicesWithConnections: boolean;
  address: any;
  applicationEntitiesData: any;
  isLoadingApplicationEntities: boolean;
  refetchApplicationEntities: () => Promise<any>;
  pagination: {
    pageIndex: number;
    pageSize: number;
  };
  setPagination: React.Dispatch<
    React.SetStateAction<{
      pageIndex: number;
      pageSize: number;
    }>
  >;
  sortConfig: {
    sort: string;
    direction: 'ASC' | 'DESC';
  };
  setSortConfig: React.Dispatch<
    React.SetStateAction<{
      sort: string;
      direction: 'ASC' | 'DESC';
    }>
  >;
  selectedDeviceId: number | null;
  refetchDevicesWithConnections: () => Promise<any>;
}) {
  const { t } = useTranslation(['Organization', 'Common']);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);

  const startAEMutation = useStartApplicationEntity();
  const stopAEMutation = useStopApplicationEntity();
  const deleteAEMutation = useDeleteApplicationEntity();
  const updateAEMutation = useUpdateComprehensiveApplicationEntity();

  const applicationEntities = applicationEntitiesData?.content || [];
  const totalPages = applicationEntitiesData?.totalPages || 0;

  // Modal states
  const [viewModalState, setViewModalState] = useState<{
    open: boolean;
    ae: ApplicationEntity | null;
  }>({
    open: false,
    ae: null,
  });

  const [configureModalState, setConfigureModalState] = useState<{
    open: boolean;
    ae: ApplicationEntity | null;
  }>({
    open: false,
    ae: null,
  });

  const [accessModalState, setAccessModalState] = useState<{
    open: boolean;
    ae: ApplicationEntity | null;
  }>({
    open: false,
    ae: null,
  });

  const [deleteModalState, setDeleteModalState] = useState<{
    open: boolean;
    ae: ApplicationEntity | null;
  }>({
    open: false,
    ae: null,
  });

  // Action handlers
  const handleStartAE = async (ae: ApplicationEntity) => {
    try {
      await startAEMutation.mutateAsync({
        deviceId: ae.deviceId,
        aeId: ae.id,
      });
      messageI18n.success('devices.aeStarted', { aeTitle: ae.aeTitle });
      await refetchApplicationEntities();
      await refetchDevicesWithConnections();
    } catch (error: any) {
      console.error('Error starting AE:', error);
      message.error(error?.message || tToast('common.genericError'));
    }
  };

  const handleStopAE = async (ae: ApplicationEntity) => {
    try {
      await stopAEMutation.mutateAsync({
        deviceId: ae.deviceId,
        aeId: ae.id,
      });
      messageI18n.success('devices.aeStopped', { aeTitle: ae.aeTitle });
      await refetchApplicationEntities();
      await refetchDevicesWithConnections();
    } catch (error: any) {
      console.error('Error stopping AE:', error);
      message.error(error?.message || tToast('common.genericError'));
    }
  };

  const handleDeleteAE = async () => {
    if (!deleteModalState.ae) {
      return;
    }

    try {
      await deleteAEMutation.mutateAsync({
        deviceId: deleteModalState.ae.deviceId,
        aeId: deleteModalState.ae.id,
      });
      messageI18n.success('devices.aeDeleted');
      setDeleteModalState({ open: false, ae: null });
      await refetchApplicationEntities();
      await refetchDevicesWithConnections();
    } catch (error: any) {
      console.error('Error deleting AE:', error);
      message.error(error?.message || tToast('common.genericError'));
    }
  };

  const handleConfigureAE = async (
    device: any,
    connection: any,
    ae: any,
    formData: any
  ) => {
    await refetchApplicationEntities();
  };

  const handleUpdateAcceptedCallingAETitles = async (
    deviceId: string,
    connectionId: string,
    aeId: string,
    acceptedTitles: Array<{
      callingAeTitle: string;
      doctorId?: string;
      doctorFirstName?: string;
      doctorLastName?: string;
      doctorRole?: string;
      organization?: string;
      enabled: boolean;
    }>
  ) => {
    await refetchApplicationEntities();
    await refetchDevicesWithConnections();
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: t('pacsTab.table.name'),
        cell: (info) => {
          const name = info.getValue();
          return (
            <div className="text-sm text-gray-900 dark:text-gray-300">
              {name || '-'}
            </div>
          );
        },
      }),
      columnHelper.accessor('aeTitle', {
        header: t('pacsTab.table.aeTitle'),
        cell: (info) => (
          <div className="flex items-center gap-2">
            <Activity className="h-4 w-4 text-blue-600" />
            {/* The plain name is already its own column, so only show the AE title here. */}
            <span className="font-mono text-sm font-medium text-gray-900 dark:text-white">
              {info.getValue()}
            </span>
          </div>
        ),
      }),
      columnHelper.display({
        id: 'manufacturer-model',
        header: t('pacsTab.table.manufacturerModel'),
        cell: ({ row }) => {
          const ae = row.original;
          const hasManufacturer = ae.manufacturer && ae.manufacturer.trim();
          const hasModel = ae.model && ae.model.trim();

          if (!hasManufacturer && !hasModel) {
            return (
              <div className="text-sm text-gray-400 dark:text-gray-500">-</div>
            );
          }

          return (
            <div className="text-sm">
              {hasManufacturer && (
                <div className="font-medium text-gray-900 dark:text-white">
                  {ae.manufacturer}
                </div>
              )}
              {hasModel && (
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {ae.model}
                </div>
              )}
            </div>
          );
        },
      }),
      columnHelper.accessor('aeType', {
        header: t('pacsTab.table.aeType'),
        cell: (info) => {
          const aeType = info.getValue();

          if (!aeType || aeType === null) {
            return <Badge text={t('pacsTab.table.mix')} variant="neutral" />;
          }

          const displayType =
            aeType.toUpperCase() === 'OB'
              ? t('pacsTab.table.standard')
              : aeType.toUpperCase() === 'BLIND'
                ? t('pacsTab.table.blind')
                : aeType;

          const variant =
            aeType.toUpperCase() === 'OB'
              ? 'success'
              : aeType.toUpperCase() === 'BLIND'
                ? 'warning'
                : 'neutral';

          return <Badge text={displayType} variant={variant} />;
        },
      }),
      // columnHelper.accessor('device.deviceName', {
      //   header: 'Device',
      //   cell: (info) => {
      //     const device = info.row.original.device;
      //     return (
      //       <div className="flex items-center gap-2">
      //         <Server className="h-4 w-4 text-gray-400" />
      //         <div>
      //           <div className="text-sm font-medium text-gray-900 dark:text-gray-300">
      //             {device.deviceName}
      //           </div>
      //           <Badge
      //             text={device.status}
      //             variant={device.status === 'ACTIVE' ? 'success' : 'neutral'}
      //           />
      //         </div>
      //       </div>
      //     );
      //   },
      // }),
      // columnHelper.accessor('connection.connectionName', {
      //   header: 'Connection',
      //   cell: (info) => {
      //     const connection = info.row.original.connection;
      //     return (
      //       <div className="flex items-center gap-2">
      //         <Cable className="h-4 w-4 text-gray-400" />
      //         <div>
      //           <div className="text-sm text-gray-900 dark:text-gray-300">
      //             {connection.connectionName}
      //           </div>
      //           <div className="flex items-center gap-2 text-xs text-gray-500">
      //             <span>
      //               {address?.data?.static_ips &&
      //               address?.data?.static_ips.length > 0
      //                 ? address?.data?.static_ips
      //                     .map(
      //                       (ip: any) => `${ip?.address}:${connection?.port}`
      //                     )
      //                     .join(', ')
      //                 : `${connection?.hostname}:${connection?.port}`}
      //             </span>
      //             {connection.tlsEnabled && (
      //               <Shield className="h-3 w-3 text-green-600" />
      //             )}
      //           </div>
      //         </div>
      //       </div>
      //     );
      //   },
      // }),
      columnHelper.accessor('status', {
        header: t('pacsTab.table.status'),
        cell: (info) => {
          const status = info.getValue();
          return getAEStatusBadge(mapAEStatus(status));
        },
      }),
      columnHelper.accessor('acceptedCallingAeTitles', {
        header: t('pacsTab.table.acceptedCallers'),
        cell: (info) => {
          const titles = info.getValue();
          return (
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-gray-400" />
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {titles.length === 0
                  ? t('pacsTab.table.allWildcard')
                  : t('pacsTab.table.configured', { count: titles.length })}
              </span>
            </div>
          );
        },
      }),
      columnHelper.display({
        id: 'actions',
        header: t('pacsTab.table.actions'),
        cell: ({ row }) => {
          const ae = row.original;
          const isActive = ae.status === 'ACTIVE';
          const isDeviceActive = ae.device.status === 'ACTIVE';

          return (
            <div className="flex justify-end">
              <ActionMenu>
                <ActionMenuItem
                  icon={Eye}
                  label={t('pacsTab.table.viewDetails')}
                  onClick={() => {
                    setViewModalState({ open: true, ae });
                    setMenuOpenId(null);
                  }}
                  className=""
                  disabled={false}
                  onClose={() => setMenuOpenId(null)}
                />
                <ActionMenuItem
                  icon={isActive ? Square : Play}
                  label={isActive ? t('pacsTab.table.stop') : t('pacsTab.table.start')}
                  onClick={() => {
                    if (isActive) {
                      handleStopAE(ae);
                    } else {
                      handleStartAE(ae);
                    }
                    setMenuOpenId(null);
                  }}
                  className={
                    !isDeviceActive
                      ? 'text-gray-400'
                      : isActive
                        ? 'text-amber-700'
                        : 'text-green-700'
                  }
                  disabled={
                    startAEMutation.isPending || stopAEMutation.isPending
                  }
                  onClose={() => setMenuOpenId(null)}
                />
                <ActionMenuItem
                  icon={Settings}
                  label={t('pacsTab.table.configure')}
                  onClick={() => {
                    setConfigureModalState({ open: true, ae });
                    setMenuOpenId(null);
                  }}
                  className={isActive ? 'text-gray-400' : ''}
                  disabled={isActive}
                  onClose={() => setMenuOpenId(null)}
                />
                <ActionMenuItem
                  icon={Users}
                  label={t('pacsTab.table.manageAccess')}
                  onClick={() => {
                    setAccessModalState({ open: true, ae });
                    setMenuOpenId(null);
                  }}
                  className=""
                  disabled={false}
                  onClose={() => setMenuOpenId(null)}
                />
                <ActionMenuItem
                  icon={Trash2}
                  label={t('pacsTab.table.delete')}
                  onClick={() => {
                    setDeleteModalState({ open: true, ae });
                    setMenuOpenId(null);
                  }}
                  className={isActive ? 'text-gray-400' : 'text-red-700'}
                  disabled={isActive || deleteAEMutation.isPending}
                  onClose={() => setMenuOpenId(null)}
                />
              </ActionMenu>
            </div>
          );
        },
      }),
    ],
    [
      startAEMutation.isPending,
      stopAEMutation.isPending,
      deleteAEMutation.isPending,
      t,
    ]
  );

  const table = useReactTable({
    data: applicationEntities,
    columns,
    state: {
      pagination,
    },
    pageCount: totalPages,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
  });

  if (isLoadingApplicationEntities) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 dark:text-red-400 mb-4">
          {t('pacsTab.aeLoadError')}
        </div>
        <Button
          onClick={() => refetchApplicationEntities()}
          variant="secondary"
          size="sm"
        >
          {t('pacsTab.retry')}
        </Button>
      </div>
    );
  }

  const transformAEForModal = (ae: ApplicationEntity | null) => {
    if (!ae) {
      return null;
    }

    // Handle null aeType - if null, set dicomType to empty string to match ae-creation-optimal.tsx logic
    let dicomType = '';
    if (ae.aeType) {
      dicomType = ae.aeType.toLowerCase() === 'blind' ? 'blind' : 'standard';
    }

    return {
      ...ae,
      id: ae.id.toString(),
      status: mapAEStatus(ae.status),
      dicomType,
      syntaxConfig:
        ae.transferCapabilities && ae.transferCapabilities.length > 0
          ? transformAETransferCapabilities(ae.transferCapabilities)
          : defaultSyntaxConfig,
      acceptedCallingAeTitles: ae.acceptedCallingAeTitles || [],
    };
  };

  const transformDeviceForModal = (device: DeviceInfo | null) => {
    if (!device) {
      return null;
    }
    return {
      ...device,
      id: device.id.toString(),
      name: device.deviceName,
    };
  };

  const transformConnectionForModal = (connection: ConnectionInfo | null) => {
    if (!connection) {
      return null;
    }
    return {
      ...connection,
      id: connection.id.toString(),
      name: connection.connectionName,
    };
  };

  return (
    <div>
      {/* Header with Add Machine button */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Activity className="h-[18px] w-[18px] text-blue-600" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t('pacsTab.machines')}
          </h2>
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500 dark:bg-gray-700 dark:text-gray-400">
            {applicationEntitiesData?.totalElements ?? applicationEntities.length}
          </span>
        </div>
        {applicationEntities.length > 0 && !isLoadingApplicationEntities && (
          <Button
            onClick={() => setIsCreateModalOpen(true)}
            variant="primary"
            size="sm"
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            {t('pacsTab.addMachine')}
          </Button>
        )}
      </div>

      {/* Table or Empty State */}
      {applicationEntities.length === 0 && !isLoadingApplicationEntities ? (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-900/50 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-700">
          <Activity className="h-12 w-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            {t('Common:noMachinesFound')}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {t('Common:noMachinesFoundHint')}
          </p>
          <Button
            onClick={() => setIsCreateModalOpen(true)}
            variant="primary"
            size="sm"
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            {t('pacsTab.addMachine')}
          </Button>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <Table
            table={table}
            isLoading={isLoadingApplicationEntities}
            columnsLength={columns.length}
          />
        </div>
      )}

      {/* Application Entity Creation Modal */}
      <div
        className={`fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center ${
          isCreateModalOpen ? '' : 'hidden'
        }`}
        onClick={() => setIsCreateModalOpen(false)}
      >
        <div
          className="w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-lg shadow-xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-2">
            <AECreationOptimalForm
              devicesData={devicesDataWithConnections}
              isLoadingDevices={isLoadingDevicesWithConnections}
              isModal={true}
              onCancel={() => setIsCreateModalOpen(false)}
              onEntityCreated={async (entity) => {
                await refetchApplicationEntities();
                setIsCreateModalOpen(false);
              }}
              address={address}
              selectedDeviceId={selectedDeviceId}
              isAllDevicesSelected={selectedDeviceId === null}
            />
          </div>
        </div>
      </div>

      {/* View Modal */}
      {viewModalState.ae && (
        <AEViewModal
          isOpen={viewModalState.open}
          onClose={() => setViewModalState({ open: false, ae: null })}
          ae={transformAEForModal(viewModalState.ae) as any}
          device={transformDeviceForModal(viewModalState.ae?.device) as any}
          connection={
            transformConnectionForModal(viewModalState.ae?.connection) as any
          }
        />
      )}

      {/* Configure Modal */}
      {configureModalState.ae && (
        <ConfigureAEModal
          isOpen={configureModalState.open}
          onClose={() => setConfigureModalState({ open: false, ae: null })}
          ae={transformAEForModal(configureModalState.ae) as any}
          device={
            transformDeviceForModal(configureModalState.ae?.device) as any
          }
          connection={
            transformConnectionForModal(
              configureModalState.ae?.connection
            ) as any
          }
          onUpdate={handleConfigureAE}
          onSyntaxUpdate={(deviceId, connectionId, aeId, syntaxConfig) => {
            // This will be handled by the modal itself
          }}
        />
      )}

      {/* Access Management Modal */}
      {accessModalState.ae && (
        <AcceptedCallingAETitlesModal
          isOpen={accessModalState.open}
          onClose={() => setAccessModalState({ open: false, ae: null })}
          ae={transformAEForModal(accessModalState.ae) as any}
          device={transformDeviceForModal(accessModalState.ae?.device) as any}
          connection={
            transformConnectionForModal(accessModalState.ae?.connection) as any
          }
          onUpdate={handleUpdateAcceptedCallingAETitles}
        />
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={deleteModalState.open}
        onClose={() => setDeleteModalState({ open: false, ae: null })}
        type="ae"
        item={deleteModalState.ae as any}
        onDelete={handleDeleteAE}
      />
    </div>
  );
}
